// Constants for the MeetingPoint application
// Based on documentation from docs/

// Message Types
export const MESSAGE_TYPE_TEXT = 'message';
export const MESSAGE_TYPE_LINK = 'link';
export const MESSAGE_TYPE_TYPING = 'typing';
export const MESSAGE_TYPE_USER_UPDATE = 'user-update';
export const MESSAGE_TYPE_REMOVE_USER = 'remove-user';
export const MESSAGE_TYPE_MEDIA = 'media';
export const MESSAGE_TYPE_ASK_REFRESH = 'ask-refresh';
export const MESSAGE_TYPE_DO_REFRESH = 'do-refresh';
export const MESSAGE_TYPE_PING = 'ping';
export const MESSAGE_TYPE_PONG = 'pong';
export const MESSAGE_TYPE_RECONNECT_OFFER = 'reconnect-offer';
export const MESSAGE_TYPE_CALL_RESTART_REQUEST = 'call-restart-request';
export const MESSAGE_TYPE_FILE = 'file';
export const MESSAGE_TYPE_CONFIRMATION_REQUEST = 'confirmation-request';
export const MESSAGE_TYPE_CONFIRMATION_RESPONSE = 'confirmation-response';

// File Types
export const IMAGE_TYPE = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
export const VIDEO_TYPE = ['mp4', 'mov', 'mkv', 'wmv', 'avi', 'webm'];
export const AUDIO_TYPE = ['mp3', 'wav', 'ogg', 'aac', 'm4a'];
export const FILE_TYPE = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'zip', 'rar'];
export const MEDIA_TYPE = [...IMAGE_TYPE, ...VIDEO_TYPE];

// Transfer Settings
export const CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB chunks
export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

// Connection Settings
export const PING_TIMEOUT_MS = 45000; // 45 seconds
export const MAX_RECONNECT_ATTEMPTS = 5;
export const RECONNECT_BACKOFF_BASE = 2000; // 2 seconds
export const RECONNECT_BACKOFF_CAP = 30000; // 30 seconds
export const HEARTBEAT_INTERVAL = 30000; // 30 seconds
export const PEER_UPDATE_DEBOUNCE = 1000; // 1 second
export const MAX_CONCURRENT_CALLS = 8;
export const CALL_DUPLICATE_THRESHOLD = 1500; // 1.5 seconds

// Media Constraints
// Ultra-low bandwidth mode: Target 200-250 kbps
// Reduced resolution and frame rate for bandwidth optimization
export const VIDEO_CONSTRAINTS = {
  width: { ideal: 140, max: 140 },
  height: { ideal: 220, max: 220 },
  frameRate: { ideal: 8, max: 10 },
  facingMode: 'user', // Front camera for mobile (portrait)
  aspectRatio: { ideal: 0.67 } // Portrait ratio (2:3)
};

export const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sampleRate: 16000, // Reduced from 48000 for lower bandwidth
  channelCount: 1 // Mono audio for bandwidth optimization
};

export const SCREEN_CONSTRAINTS = {
  video: {
    width: { ideal: 480, max: 480 },
    height: { ideal: 270, max: 270 },
    frameRate: { ideal: 8, max: 10 },
    cursor: 'always'
  },
  audio: false
};

// Room Settings
export const MAX_ROOM_CAPACITY = 4;

// User Color Palette
export const USER_COLORS = [
  '#e74c3c', '#9b59b6', '#2980b9', '#1abc9c',
  '#27ae60', '#f39c12', '#d35400', '#c0392b',
  '#8e44ad', '#3498db', '#f1c40f', '#e67e22',
  '#c0392b', '#7f8c8d'
];

// Emoji List (200+ emojis as per documentation)
export const EMOJI_LIST = [
  "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣",
  "😊", "😇", "🙂", "🙃", "😉", "😌", "😍", "🥰",
  "😘", "😗", "😙", "😚", "😋", "😛", "😝", "😜",
  "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳", "😏",
  "😒", "😞", "😔", "😟", "😕", "🙁", "☹️", "😣",
  "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠",
  "😡", "🤬", "🤯", "😳", "🥵", "🥶", "😱", "😨",
  "😰", "😥", "😓", "🤗", "🤔", "🤭", "🤫", "🤥",
  "😶", "😐", "😑", "😬", "🙄", "😯", "😦", "😧",
  "😮", "😲", "🥱", "😴", "🤤", "😪", "😵", "🤐",
  "🥴", "🤢", "🤮", "🤧", "😷", "🤒", "🤕", "🤑",
  "🤠", "😈", "👿", "👹", "👺", "🤡", "💩", "👻",
  "💀", "☠️", "👽", "👾", "🤖", "🎃", "😺", "😸",
  "😹", "😻", "😼", "😽", "🙀", "😿", "😾", "👋",
  "🤚", "🖐️", "✋", "🖖", "👌", "🤏", "✌️", "🤞",
  "🤟", "🤘", "🤙", "👈", "👉", "👆", "🖕", "👇",
  "☝️", "👍", "👎", "✊", "👊", "🤛", "🤜", "👏",
  "🙌", "👐", "🤲", "🤝", "🙏", "✍️", "💅", "🤳",
  "💪", "🦾", "🦿", "🦵", "🦶", "👂", "🦻", "👃",
  "🧠", "🫀", "🫁", "🦷", "🦴", "👀", "👁️", "👅",
  "👄", "💋", "🩸", "🔥", "💥", "💫", "💦", "💨",
  "🌟", "⭐", "✨", "⚡", "☄️", "💯", "🎉", "🎊",
  "🎈", "🎂", "🎀", "🎁", "🏆", "🥇", "🥈", "🥉",
  "⚽", "⚾", "🥎", "🏀", "🏐", "🏈", "🏉", "🎾",
  "❤️", "🧡", "💛", "💚", "💙", "💜", "🤎", "🖤",
  "🤍", "💔", "❣️", "💕", "💞", "💓", "💗", "💖",
  "💘", "💝", "🌹", "🌺", "🌻", "🌷", "🌸"
];

// Sound event types
export const SOUND_TYPES = {
  MESSAGE: 'ding',
  FILE: 'dudung',
  USER_JOIN: 'tupulutu',
  USER_LEAVE: 'tuptulu'
};
